import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ITEM_LABEL } from "@/components/DamageExtras";
import { Pencil, Sparkles } from "lucide-react";
import { toast } from "sonner";

function EditForm({ r, onDone, onCancel }) {
  const [f, setF] = useState({ description: r.description, severity: r.severity, item: r.item, estimated_cost: r.estimated_cost ?? "" });
  const save = async () => {
    try {
      await api.put(`/damage-reports/${r.id}/details`, { ...f, estimated_cost: f.estimated_cost === "" ? null : Number(f.estimated_cost) });
      toast.success("Report updated"); onDone();
    } catch (err) { toast.error(apiError(err)); }
  };
  const sel = "h-9 rounded-md border border-slate-200 bg-white px-2 text-sm";
  return (
    <div className="mt-3 space-y-2 border border-slate-200 rounded-lg p-3" data-testid={`damage-edit-form-${r.id}`}>
      <Textarea rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid={`damage-edit-description-${r.id}`} />
      <div className="flex flex-wrap gap-2">
        <select className={sel} value={f.severity} onChange={(e) => setF({ ...f, severity: e.target.value })} data-testid={`damage-edit-severity-${r.id}`}>
          {["minor", "moderate", "severe"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={sel} value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} data-testid={`damage-edit-item-${r.id}`}>
          {Object.entries(ITEM_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Input type="number" min={0} step="0.01" className="h-9 w-32" placeholder="Est. cost £" value={f.estimated_cost}
          onChange={(e) => setF({ ...f, estimated_cost: e.target.value })} data-testid={`damage-edit-cost-${r.id}`} />
        <Button size="sm" className="rounded-full ns-btn-primary" onClick={save} data-testid={`damage-edit-save-${r.id}`}>Save</Button>
        <Button size="sm" variant="ghost" className="rounded-full" onClick={onCancel} data-testid={`damage-edit-cancel-${r.id}`}>Cancel</Button>
      </div>
    </div>
  );
}

export function EstimatePanel({ r, onChange }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const est = r.ai_estimate;
  const suggest = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/damage-reports/${r.id}/estimate`); toast.success(`AI estimate £${data.low}–£${data.high}`); onChange(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3">
      {est && (
        <div className="text-xs bg-violet-50 border border-violet-100 text-violet-900 rounded-lg px-3 py-2" data-testid={`damage-estimate-${r.id}`}>
          <span className="font-medium inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI estimate £{est.low}–£{est.high}</span>
          {" "}· {est.severity} · {ITEM_LABEL[est.item] || "Other"}{est.reason ? ` — ${est.reason}` : ""}
          <div className="mt-1 text-[11px] text-violet-700/80" data-testid={`damage-estimate-calibration-${r.id}`}>
            {est.calibrated_from ? `Calibrated from ${est.calibrated_from} past charge${est.calibrated_from === 1 ? "" : "s"}${est.calibration_factor ? ` · ${ITEM_LABEL[est.item]} adjusted ×${est.calibration_factor}` : ""}` : "No past charges yet — general UK pricing"}
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-2">
        <button onClick={suggest} disabled={busy} data-testid={`damage-suggest-cost-${r.id}`}
          className="text-[11px] uppercase tracking-wider px-3 py-1 rounded-full border border-violet-200 text-violet-700 hover:bg-violet-50 transition-colors inline-flex items-center gap-1 disabled:opacity-50">
          <Sparkles className={`w-3 h-3 ${busy ? "animate-pulse" : ""}`} /> {busy ? "Estimating…" : est ? "Re-estimate" : "Suggest cost"}
        </button>
        {!editing && (
          <button onClick={() => setEditing(true)} data-testid={`damage-edit-btn-${r.id}`}
            className="text-[11px] uppercase tracking-wider px-3 py-1 rounded-full border border-slate-200 hover:border-teal-600 hover:text-teal-700 transition-colors inline-flex items-center gap-1">
            <Pencil className="w-3 h-3" /> Edit
          </button>
        )}
      </div>
      {editing && <EditForm r={r} onCancel={() => setEditing(false)} onDone={() => { setEditing(false); onChange(); }} />}
    </div>
  );
}
